import {Pinia} from 'pinia'
import {Router, RouteLocationNormalizedLoaded, RouteLocationOptions, RouteLocationRaw} from 'vue-router'
import {useRouteStore} from '@toolify/client/src/stores/RouteStore/useRouteStore'
import {isStackableRoute} from '@toolify/client/src/setup/router/hooks/isStackableOverlayNavigation'

/**
 * A stackable overlay takes over `router.currentRoute` while the page it opened over stays mounted. That page's
 * links are written without `workspaceName` and let vue-router inherit it from the current location, so an
 * overlay route without one makes them throw `Missing required param`.
 *
 * While an overlay is on top, missing params are therefore inherited from the page underneath instead. Params
 * the overlay route does have still win, so links inside the overlay resolve against the overlay itself.
 */
export function applyStackableRouteParamInheritance(router: Router, pinia: Pinia) {
  const routeStore = useRouteStore(pinia)
  const resolve = router.resolve.bind(router)
  const push = router.push.bind(router)
  const replace = router.replace.bind(router)

  function getInheritanceLocation(): RouteLocationNormalizedLoaded | null {
    const currentRoute = router.currentRoute.value
    if(!isStackableRoute(currentRoute)) {
      return null
    }
    const baseRoute = routeStore.lastNonStackableRoute
    if(!baseRoute || baseRoute.fullPath === currentRoute.fullPath) {
      return null
    }
    return {
      ...currentRoute,
      params: {
        ...baseRoute.params,
        ...currentRoute.params,
      },
    } as RouteLocationNormalizedLoaded
  }

  function withInheritedParams(to: RouteLocationRaw): RouteLocationRaw {
    const inheritanceLocation = getInheritanceLocation()
    if(!inheritanceLocation) {
      return to
    }
    try {
      const resolved = resolve(to, inheritanceLocation)
      const {replace: shouldReplace, force, state} = (typeof to === 'object' && to !== null
        ? to
        : {}) as RouteLocationOptions
      return {
        path: resolved.path,
        query: resolved.query,
        hash: resolved.hash,
        ...(shouldReplace === undefined ? {} : {replace: shouldReplace}),
        ...(force === undefined ? {} : {force}),
        ...(state === undefined ? {} : {state}),
      }
    } catch {
      return to
    }
  }

  router.resolve = ((to: RouteLocationRaw, currentLocation?: RouteLocationNormalizedLoaded) => {
    return resolve(to, currentLocation || getInheritanceLocation() || undefined)
  }) as Router['resolve']

  router.push = ((to: RouteLocationRaw) => push(withInheritedParams(to))) as Router['push']
  router.replace = ((to: RouteLocationRaw) => replace(withInheritedParams(to))) as Router['replace']
}
